/**
 * `/api/auth` payloads.
 *
 * One endpoint: minting the short-lived nonce token that every value-moving
 * request has to present. The token is a stateless JWT, so this endpoint hands
 * out something replayable for its whole lifetime — single use is enforced at
 * the endpoints that act on it (`consumeNonce`), never here.
 */
import { z } from "zod";

import { suiAddressSchema } from "../primitives";

/**
 * Nonce request.
 *
 * The strict 64-hex address form is the security boundary the Next.js original
 * added by hand: without it a nonce is mintable for any arbitrary string.
 */
export const getJwtNonceRequestSchema = z.object({
  owner: suiAddressSchema,
});

/** Resolved nonce request. */
export type GetJwtNonceRequest = z.infer<typeof getJwtNonceRequestSchema>;

/**
 * Nonce response.
 *
 * `nonce`/`nonceToken` are present even on failure (as empty strings) because
 * that is the shape clients already branch on — they check `error` first, but
 * older builds read the fields unconditionally.
 */
export const getJwtNonceResponseSchema = z.object({
  nonce: z.string(),
  nonceToken: z.string(),
  error: z.string().optional(),
});

/** Resolved nonce response. */
export type GetJwtNonceResponse = z.infer<typeof getJwtNonceResponseSchema>;
